import type { ParseKeys, TFunction } from 'i18next';
import type { TaskFlowHistoryOnDate, TaskFlowHistoryType } from '../../../api/sprints-client/sprints-client.type';
import {
  type FlowByPhaseDataOverTime,
  Measure,
  type WorkPeriod,
  type WorkPeriodGoal,
} from '../../../api/work-periods-client/work-periods-client.type';
import {
  type DeliveryConfidenceChartDataPoint,
  Metric,
  type PaceChartDataPoint,
  WorkPaceChartType,
} from '../work-periods.type';
import type { KeyMeasuresInput } from './single-assessment-flora.type';

/**
 * Gets the Flora prompt key based on the chart type for work pace
 * @param {WorkPaceChartType} chartType - Stable, non-translated chart type key
 * @returns {string} The Flora prompt i18n key
 */
const getWorkPaceFloraPromptKey = (chartType: WorkPaceChartType): ParseKeys<'translation'> => {
  return chartType === WorkPaceChartType.BurnUp
    ? 'flora.work_periods.work_pace_burn_up_chart_prompt'
    : 'flora.work_periods.work_pace_burn_down_chart_prompt';
};

/**
 * Gets the Flora prompt key based on the chart type for delivery confidence
 * @param {string} chartType - 'burn_up' or 'burn_down'
 * @returns {string} The Flora prompt i18n key
 */
const getDeliveryConfidenceFloraPromptKey = (chartType: string): ParseKeys<'translation'> => {
  const isBurnUp = chartType === 'burn_up';
  return isBurnUp
    ? 'flora.work_periods.delivery_confidence_burn_up_chart_prompt'
    : 'flora.work_periods.delivery_confidence_burn_down_chart_prompt';
};

/**
 * Gets work period context for Flora analysis
 * @param {WorkPeriod | undefined} workPeriod - The work period to extract context from
 * @returns {Record<string, any>} Work period context object or empty object
 */
const getWorkPeriodContext = (workPeriod: WorkPeriod | undefined): Record<string, any> => {
  if (!workPeriod) return {};
  return {
    work_period: {
      name: workPeriod.name,
      start_date: workPeriod.start_date,
      end_date: workPeriod.end_date,
    },
  };
};

/**
 * Gets the work unit label based on the selected metric
 * @param {Metric | string} selectedMetric - The metric to get the label for
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} The localized work unit label
 */
const getWorkUnitLabel = (selectedMetric: Metric | string, t: TFunction): string => {
  return selectedMetric === Metric.Points ? t('common.estimation') : t('common.work_units');
};

/**
 * Formats assessment view data for Flora analysis
 * @param {{ health_scores?: { score_by_points?: number | string } } | undefined} scoreData - Health score data
 * @param {KeyMeasuresInput | undefined} keyMeasuresData - Key measures data
 * @param {WorkPeriodGoal[] | undefined} goals - Work period goals
 * @param {boolean} showScoreSection - Whether the score section is enabled
 * @param {string | null | undefined} aiInsights - AI insights if available
 * @param {WorkPeriod | undefined} workPeriod - Work period context
 * @returns {Record<string, any>} Formatted data for Flora
 */
const formatAssessmentDataForFlora = (
  scoreData: { health_scores?: { score_by_points?: number | string } } | undefined,
  keyMeasuresData: KeyMeasuresInput | undefined,
  goals: WorkPeriodGoal[] | undefined,
  showScoreSection: boolean,
  aiInsights: string | null | undefined,
  workPeriod: WorkPeriod | undefined,
): Record<string, any> => {
  const result: Record<string, any> = {
    ...getWorkPeriodContext(workPeriod),
  };

  // Only add health score if showScoreSection is enabled
  if (showScoreSection && scoreData?.health_scores?.score_by_points !== undefined) {
    result.health_score = scoreData.health_scores.score_by_points;
  }

  // Add AI insights if available
  if (aiInsights) {
    result.ai_insights = aiInsights;
  }

  // Add key measures with human-readable names
  if (keyMeasuresData) {
    result.key_measures = {
      strategy: keyMeasuresData[Measure.Strategy],
      complexity: keyMeasuresData[Measure.Complexity],
      quality: keyMeasuresData[Measure.Quality],
      scope_creep: keyMeasuresData[Measure.ScopeCreep],
      readiness: keyMeasuresData[Measure.Readiness],
      independence: keyMeasuresData[Measure.Independence],
    };
  }

  // Add goals summary
  if (goals && goals.length > 0) {
    const completedGoals = goals.filter((g) => g.completed).length;
    const completionRate = goals.length > 0 ? Math.round((completedGoals / goals.length) * 100) : 0;
    result.goals = {
      total: goals.length,
      completed: completedGoals,
      completion_rate: `${completionRate}%`,
      items: goals.map((g) => ({
        description: g.description,
        completed: g.completed,
      })),
    };
  }

  return result;
};

/**
 * Formats flow-by-phase data for Flora analysis
 * @param {FlowByPhaseDataOverTime | undefined} flowData - Raw flow data from useFlowByPhaseData hook
 * @param {string | null} selectedDate - Currently selected date
 * @param {Metric | string} selectedMetric - Tasks or Points
 * @param {WorkPeriod | undefined} workPeriod - Work period context
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {Record<string, any>} Formatted data for Flora
 */
const formatFlowByPhaseDataForFlora = (
  flowData: FlowByPhaseDataOverTime | undefined,
  selectedDate: string | null,
  selectedMetric: Metric | string,
  workPeriod: WorkPeriod | undefined,
  t: TFunction,
): Record<string, any> => {
  if (!flowData) return getWorkPeriodContext(workPeriod);

  const result: Record<string, any> = {
    ...getWorkPeriodContext(workPeriod),
    units: getWorkUnitLabel(selectedMetric, t),
    phase_distribution_over_time: {},
  };

  // Get the latest data point or the selected date
  const dates = Object.keys(flowData).sort();
  const targetDate = selectedDate && flowData[selectedDate] ? selectedDate : dates[dates.length - 1];

  if (targetDate && flowData[targetDate]) {
    const phaseData = flowData[targetDate];
    result.current_distribution = {
      date: targetDate,
      backlog: phaseData.tasks_backlog || 0,
      blocked: phaseData.tasks_blocked || 0,
      in_progress: phaseData.tasks_wip || 0,
      in_review: phaseData.tasks_review || 0,
      in_test: phaseData.tasks_test || 0,
      deployable: phaseData.tasks_deployable || 0,
      done: phaseData.tasks_done || 0,
    };

    // Calculate totals and percentages
    const total = Object.values(result.current_distribution)
      .filter((v): v is number => typeof v === 'number')
      .reduce((sum, val) => sum + val, 0);

    result.current_distribution.total = total;
    if (total > 0) {
      const donePercentage = Math.round(((result.current_distribution.done || 0) / total) * 100);
      result.current_distribution.done_percentage = `${donePercentage}%`;
    } else {
      result.current_distribution.done_percentage = '0%';
    }
  }

  // Include all trend data for comprehensive time series analysis
  result.trend_data = dates.map((date) => {
    const phaseData = flowData[date];
    return {
      date,
      done: phaseData?.tasks_done || 0,
      in_progress: phaseData?.tasks_wip || 0,
      backlog: phaseData?.tasks_backlog || 0,
    };
  });

  return result;
};

/**
 * Formats work pace data for Flora analysis
 * @param {PaceChartDataPoint[]} chartData - Transformed pace chart data
 * @param {string | null} currentDate - Currently displayed date
 * @param {Metric | string} selectedMetric - Tasks or Points
 * @param {WorkPaceChartType} chartType - Stable, non-translated chart type key
 * @param {WorkPeriod | undefined} workPeriod - Work period context
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {Record<string, any>} Formatted data for Flora
 */
const formatWorkPaceDataForFlora = (
  chartData: PaceChartDataPoint[],
  currentDate: string | null,
  selectedMetric: Metric | string,
  chartType: WorkPaceChartType,
  workPeriod: WorkPeriod | undefined,
  t: TFunction,
): Record<string, any> => {
  if (!chartData || chartData.length === 0) return getWorkPeriodContext(workPeriod);

  const isBurnUp = chartType === WorkPaceChartType.BurnUp;

  const result: Record<string, any> = {
    ...getWorkPeriodContext(workPeriod),
    units: getWorkUnitLabel(selectedMetric, t),
  };

  // Get the latest data point or current date
  const targetPoint = currentDate
    ? chartData.find((point) => point.date === currentDate)
    : chartData[chartData.length - 1];

  if (targetPoint) {
    const completionPercentage =
      targetPoint.total > 0 ? Math.round((targetPoint.complete / targetPoint.total) * 100) : 0;

    result.current_status = {
      date: targetPoint.date,
      actual: targetPoint.actual,
      ideal: targetPoint.ideal,
      remaining: targetPoint.remaining,
      complete: targetPoint.complete,
      total: targetPoint.total,
      status: targetPoint.status,
      completion_percentage: `${completionPercentage}%`,
    };

    // Add burn rate analysis
    if (isBurnUp) {
      // For burn up: Show progress toward completion
      const progressGap = targetPoint.actual - targetPoint.ideal;
      result.current_status.progress_vs_ideal =
        progressGap >= 0 ? `${progressGap} ahead of ideal` : `${Math.abs(progressGap)} behind ideal`;
    } else {
      // For burn down: Show remaining work
      const remainingGap = targetPoint.remaining - targetPoint.ideal;
      result.current_status.remaining_vs_ideal =
        remainingGap <= 0 ? `${Math.abs(remainingGap)} better than ideal` : `${remainingGap} more remaining than ideal`;
    }
  }

  // Calculate burn rate from first to current point
  if (chartData.length > 1) {
    const firstPoint = chartData[0];
    const currentPoint = targetPoint || chartData[chartData.length - 1];
    const daysElapsed = chartData.findIndex((p) => p.date === currentPoint.date) + 1;

    if (daysElapsed > 0 && chartData.length > 0) {
      const workCompleted = currentPoint.complete - (firstPoint.complete || 0);
      const averageBurnRate = workCompleted / daysElapsed;
      const idealBurnRate = currentPoint.total / chartData.length;

      result.burn_rate_analysis = {
        days_elapsed: daysElapsed,
        total_days: chartData.length,
        work_completed: workCompleted,
        work_remaining: currentPoint.remaining,
        average_burn_rate: `${averageBurnRate.toFixed(2)} per day`,
        ideal_burn_rate: `${idealBurnRate.toFixed(2)} per day`,
      };

      // Only add comparison if idealBurnRate is not zero
      if (idealBurnRate > 0) {
        result.burn_rate_analysis.burn_rate_comparison =
          averageBurnRate >= idealBurnRate
            ? `${((averageBurnRate / idealBurnRate - 1) * 100).toFixed(1)}% faster than ideal`
            : `${((1 - averageBurnRate / idealBurnRate) * 100).toFixed(1)}% slower than ideal`;
      }
    }
  }

  // Analyze scope changes over time
  if (chartData.length > 1) {
    const firstPoint = chartData[0];
    const currentPoint = targetPoint || chartData[chartData.length - 1];
    const scopeChange = currentPoint.total - firstPoint.total;

    if (scopeChange !== 0) {
      const changePercentage = firstPoint.total > 0 ? Math.round((scopeChange / firstPoint.total) * 100) : 0;

      result.scope_analysis = {
        initial_scope: firstPoint.total,
        current_scope: currentPoint.total,
        total_change: scopeChange,
        change_percentage: `${changePercentage}%`,
        change_type: scopeChange > 0 ? 'scope_increase' : 'scope_decrease',
      };
    }
  }

  // Include all time series data points for comprehensive analysis
  result.time_series_data = chartData.map((point) => ({
    date: point.date,
    actual: point.actual,
    ideal: point.ideal,
    remaining: point.remaining,
    complete: point.complete,
    total: point.total,
    status: point.status,
  }));

  return result;
};

/**
 * Formats delivery confidence data for Flora analysis
 * @param {DeliveryConfidenceChartDataPoint[]} deliveryData - Merged delivery confidence chart data
 * @param {Metric | string} selectedMetric - Tasks or Points
 * @param {WorkPeriod | undefined} workPeriod - Work period context
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {Record<string, any>} Formatted data for Flora
 */
const formatDeliveryConfidenceDataForFlora = (
  deliveryData: DeliveryConfidenceChartDataPoint[],
  selectedMetric: Metric | string,
  workPeriod: WorkPeriod | undefined,
  t: TFunction,
): Record<string, any> => {
  if (!deliveryData || deliveryData.length === 0) return getWorkPeriodContext(workPeriod);

  const result: Record<string, any> = {
    ...getWorkPeriodContext(workPeriod),
    units: getWorkUnitLabel(selectedMetric, t),
  };

  // Get latest data point
  const latestPoint = deliveryData[deliveryData.length - 1];

  if (latestPoint) {
    const completionPercentage =
      latestPoint.scope > 0 ? Math.round((latestPoint.completed / latestPoint.scope) * 100) : 0;

    result.current_status = {
      date: latestPoint.date,
      completed: latestPoint.completed,
      scope: latestPoint.scope,
      completion_percentage: `${completionPercentage}%`,
    };

    // Add confidence band info if available
    if (latestPoint.band && latestPoint.band.length === 2) {
      result.current_status.confidence_band = {
        best_case: latestPoint.band[1],
        worst_case: latestPoint.band[0],
      };
    }

    if (latestPoint.average !== undefined) {
      result.current_status.historical_average = latestPoint.average;
    }
  }

  // Calculate scope change from first to last
  const firstPoint = deliveryData[0];
  if (firstPoint && latestPoint && firstPoint.scope !== latestPoint.scope) {
    const scopeChange = latestPoint.scope - firstPoint.scope;
    const changePercentage = firstPoint.scope > 0 ? Math.round((scopeChange / firstPoint.scope) * 100) : 0;

    result.scope_analysis = {
      initial_scope: firstPoint.scope,
      current_scope: latestPoint.scope,
      total_change: scopeChange,
      change_percentage: `${changePercentage}%`,
    };
  }

  // Include all progress trend data for comprehensive time series analysis
  result.progress_trend = deliveryData.map((point) => ({
    date: point.date,
    completed: point.completed,
    scope: point.scope,
  }));

  return result;
};

/**
 * Formats task flow data for Flora analysis
 * @param {TaskFlowHistoryType | undefined} taskFlowData - Raw task flow data from useWorkPeriodsTaskFlowData hook
 * @param {string | null} selectedDate - Currently selected date
 * @param {Metric | string} selectedMetric - Tasks or Points
 * @param {WorkPeriod | undefined} workPeriod - Work period context
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {Record<string, any>} Formatted data for Flora
 */
const formatTaskFlowDataForFlora = (
  taskFlowData: TaskFlowHistoryType | undefined,
  selectedDate: string | null,
  selectedMetric: Metric | string,
  workPeriod: WorkPeriod | undefined,
  t: TFunction,
): Record<string, any> => {
  if (!taskFlowData || !selectedDate) return getWorkPeriodContext(workPeriod);

  const result: Record<string, any> = {
    ...getWorkPeriodContext(workPeriod),
    units: getWorkUnitLabel(selectedMetric, t),
    date: selectedDate,
  };

  // Get tasks for the selected date
  const currentDateTasks: TaskFlowHistoryOnDate | undefined = taskFlowData[selectedDate];

  if (!currentDateTasks) {
    return result;
  }

  // Phase definitions
  const phases = ['backlog', 'blocked', 'wip', 'review', 'test', 'deployable', 'done'] as const;

  // Calculate totals across all phases
  let totalTasks = 0;
  let totalPoints = 0;

  // Build phase summary and detailed tasks
  const phaseSummary: Record<string, any> = {};
  const phaseTaskDetails: Record<string, any[]> = {};

  for (const phase of phases) {
    const phaseTasks = currentDateTasks[phase] || [];
    const taskCount = phaseTasks.length;
    const pointCount = phaseTasks.reduce((acc, task) => acc + (task.points ? Number(task.points) : 0), 0);

    totalTasks += taskCount;
    totalPoints += pointCount;

    // Summary
    phaseSummary[phase] = {
      tasks: taskCount,
      points: pointCount,
    };

    // Task details (limit to key info to avoid overwhelming Flora)
    phaseTaskDetails[phase] = phaseTasks.map((task) => ({
      code: task.code,
      name: task.name,
      type: task.type,
      points: task.points,
      created_date: task.created_date,
      epic_name: task.epic_name,
    }));
  }

  result.total_tasks = totalTasks;
  result.total_points = totalPoints;

  // Add phase summary with percentages
  result.phase_summary = phases.map((phase) => {
    const summary = phaseSummary[phase];
    const metric = selectedMetric === Metric.Tasks ? summary.tasks : summary.points;
    const totalMetric = selectedMetric === Metric.Tasks ? totalTasks : totalPoints;
    const percentage = totalMetric > 0 && metric !== undefined ? Math.round((metric / totalMetric) * 100) : 0;

    return {
      phase,
      tasks: summary.tasks,
      points: summary.points,
      percentage: `${percentage}%`,
    };
  });

  // Add task details for each phase
  result.tasks_by_phase = phaseTaskDetails;

  // Add key insights
  const completionRate =
    totalTasks > 0 && phaseSummary.done?.tasks !== undefined
      ? Math.round((phaseSummary.done.tasks / totalTasks) * 100)
      : 0;

  result.key_insights = {
    backlog_size: phaseSummary.backlog.tasks,
    blocked_count: phaseSummary.blocked.tasks,
    work_in_progress: phaseSummary.wip.tasks,
    in_review: phaseSummary.review.tasks,
    in_test: phaseSummary.test.tasks,
    deployable: phaseSummary.deployable.tasks,
    completed: phaseSummary.done.tasks,
    completion_rate: `${completionRate}%`,
  };

  return result;
};

export {
  formatAssessmentDataForFlora,
  formatDeliveryConfidenceDataForFlora,
  formatFlowByPhaseDataForFlora,
  formatTaskFlowDataForFlora,
  formatWorkPaceDataForFlora,
  getDeliveryConfidenceFloraPromptKey,
  getWorkPaceFloraPromptKey,
};
